/*
 * Cart v2 — single upsell driven by the most recently added product.
 *
 * Resolution chain (owner decision D2, 2026-08-12): complementary -> related ->
 * the server-rendered collection fallback already inside this element. First
 * non-empty wins. Verified on live 2026-08-12 that `complementary` returns zero
 * products store-wide (Search & Discovery has no complementary products
 * configured), so `related` is what actually serves today; configuring Search &
 * Discovery upgrades the result with no code change.
 *
 * "Most recently added" comes from localStorage, written on every `variant:add`.
 * The cart's own last line item is only a fallback: re-adding a product that is
 * already in the cart increments it in place and does not move it to the end of
 * cart.items, so the array order alone cannot answer the question.
 */
(function () {
  var STORAGE_KEY = 'nsCdv2LastAddedProductId';

  document.addEventListener('variant:add', function (event) {
    try {
      var items = (event.detail && event.detail.items) || [];
      var productId = items.length ? items[0].product_id : null;
      if (productId) window.localStorage.setItem(STORAGE_KEY, String(productId));
    } catch (error) {
      // localStorage can throw in private modes; the cart fallback still applies.
      console.warn('[ns-cdv2] could not record last added product:', error);
    }
  });

  /*
   * Shop Pay instalments for the cart total. TWO sources, deliberately.
   *
   * ELIGIBILITY comes from the `shopify-meta` ATTRIBUTE on Shopify's own
   * <shopify-payment-terms> element, which the theme renders from a CART form
   * (`payment_terms` accepts one). It is in the initial HTML, so the decision to
   * show the line at all is made without waiting on anything:
   *
   *   {"type":"cart","full_price":"$259.99","price_per_term":"$64.99",
   *    "number_of_payment_terms":4,"eligible":true, …}
   *
   * `full_price` is the cart total after discounts, so everything below tracks
   * the real subtotal with no arithmetic on our side.
   *
   * The AMOUNT comes from the widget's rendered shadow DOM, because the figure
   * the owner wants is the MONTHLY one and that is the one number Shopify does
   * NOT put in the attribute. The rendered sentence is "4 interest-free
   * installments, or from $23.47/mo with", and its single `span.font-bold` is
   * that monthly figure. `price_per_term` in the attribute is a different
   * number — the pay-in-4 split ($64.99 on the same $259.99 cart).
   *
   * The bold span degrades correctly rather than lying. Shopify only offers a
   * financing plan above $150; below it there is no monthly option and the
   * widget's sentence becomes the split-pay one, so the bold span is then the
   * split amount. Measured on staging 2026-08-14: a $259.99 cart gives $23.47
   * (monthly, 12mo at 15% APR) while a $99 cart gives $24.75 (= 99/4, split).
   * Either way it is the smallest instalment Shopify will actually offer, which
   * is what the copy claims.
   *
   * There is no `[n]` token for the same reason: with a monthly amount, "4" is
   * not the number of those instalments, so a count beside it would be wrong.
   *
   * A custom element rather than a load-time script: the drawer footer is
   * replaced wholesale on every cart change and the amount moves with the
   * total, so this has to re-run each time. Everything is queried through
   * `this` — the drawer ships on every page including PDPs, and a document-wide
   * lookup would let the two Shop Pay lines read each other's widget. That is
   * not hypothetical; it is the bug this branch put into the PDP banner.
   */
  if (!customElements.get('ns-cdv2-shoppay')) {
    customElements.define(
      'ns-cdv2-shoppay',
      class NsCdv2Shoppay extends HTMLElement {
        connectedCallback() {
          this._elapsed = 0;
          /*
           * Capture the template BEFORE anything can rewrite the copy element.
           * `_resolve` clears that element and rebuilds it from the template, so
           * reading it lazily inside `_resolve` reads back an emptied node and
           * renders a blank line — which is exactly what shipped to staging for
           * one push.
           */
          var copy = this.querySelector('[data-ns-cdv2-shoppay-copy]');
          this._tpl = copy ? copy.textContent.trim() : '';

          if (this._resolve()) return;
          this._poll = setInterval(this._resolve.bind(this), 250);
        }

        disconnectedCallback() {
          clearInterval(this._poll);
        }

        /* Eligibility: the attribute, present in the initial HTML. */
        _eligible() {
          var host = this.querySelector('shopify-payment-terms');
          var raw = host && host.getAttribute('shopify-meta');
          if (!raw) return null;
          try {
            return JSON.parse(raw).eligible === true;
          } catch (error) {
            console.warn('[ns-cdv2] could not parse shopify-meta:', error);
            return false;
          }
        }

        /*
         * Amount: the widget's rendered sentence, whose single bold span is the
         * monthly figure where Shopify offers financing and the split-pay
         * figure where it does not. This is the number the attribute does not
         * carry.
         */
        _amount() {
          var host = this.querySelector('shopify-payment-terms');
          var bold =
            host && host.shadowRoot
              ? host.shadowRoot
                  .querySelector('#shopify-installments-content')
                  ?.querySelector('span.font-bold')
              : null;
          var text = bold && bold.textContent.trim();
          return text || null;
        }

        _resolve() {
          var eligible = this._eligible();

          // Not eligible at all: settle on the fallback now, stop polling.
          if (eligible === false) {
            clearInterval(this._poll);
            return true;
          }

          var amount = eligible === true ? this._amount() : null;

          if (!amount) {
            this._elapsed += 250;
            // 5s, not the PDP's 8s: this sits in a footer the shopper is
            // already looking at, and a cart that cannot resolve a figure
            // should settle on the fallback quickly rather than look
            // unresolved. The shadow root is what we are waiting on here — the
            // attribute is already there.
            if (this._elapsed >= 5000) clearInterval(this._poll);
            return false;
          }

          clearInterval(this._poll);

          var live = this.querySelector('.ns-cdv2__shoppay-live');
          var fallback = this.querySelector('.ns-cdv2__shoppay-fallback');
          var copy = this.querySelector('[data-ns-cdv2-shoppay-copy]');
          if (!live || !fallback || !copy) return true;

          /*
           * The line is one merchant-authored template with an `[amount]`
           * token. The figure is wrapped in its own element so it can carry
           * weight and colour without the template needing to know any markup —
           * the merchant writes plain text and the emphasis lands on the number
           * wherever they put it.
           */
          var parts = (this._tpl || '').split('[amount]');
          copy.textContent = '';
          parts.forEach(function (part, index) {
            copy.appendChild(document.createTextNode(part));
            if (index < parts.length - 1) {
              var strong = document.createElement('strong');
              strong.className = 'ns-cdv2__shoppay-amount';
              strong.textContent = amount;
              copy.appendChild(strong);
            }
          });

          fallback.hidden = true;
          live.hidden = false;
          return true;
        }
      }
    );
  }

  /*
   * ── removing a line without leaving the page ─────────────────────────────
   *
   * The remove control is an `<a href="/cart/change?id=…&quantity=0">`, which
   * keeps it working with JS off. The theme has a handler for exactly that link
   * — `LineItemQuantity._onRemoveLinkClicked` in assets/theme.js.liquid — but it
   * never fires on v2 markup for two reasons: the listener is bound to
   * `<line-item-quantity>` and v2's remove link is that element's SIBLING rather
   * than its child, and the handler finds the anchor by walking a fixed one- or
   * two-step `parentElement` chain that v2's markup does not match. So the click
   * fell through to the browser and navigated to /cart (owner report
   * 2026-08-14).
   *
   * This is the same cart/change.js flow the theme runs for a non-cart page,
   * including the two `line-item:*` events, so LineItem still shows its pill
   * loader and plays its collapse animation. `cart:change` then re-renders the
   * drawer exactly as a quantity change does.
   *
   * On the cart PAGE the link is left alone: it is a server-rendered page, the
   * href already lands back on /cart with the item gone, and that matches what
   * the theme does for a quantity change there.
   *
   * FALLBACK RULES — this is where the first version of this handler was wrong,
   * and the bug it caused looked exactly like the one it was meant to fix.
   *
   * The href is a MUTATING url. Following it after the request has already been
   * made either removes the line twice or, once the line is gone, lands on
   * Shopify's "no valid id or line parameter" error page. The owner saw exactly
   * that on 2026-08-14: the item WAS removed and the page still navigated to an
   * error, because a single `catch` treated every failure as "the removal never
   * happened" and followed the link.
   *
   * So the link is followed in one case only: the request never reached the
   * server, which means nothing changed and the plain navigation is still the
   * correct, JS-free behaviour. Any response at all — ok or not — means the
   * cart's state is either changed or unknown, and the recovery is to re-read it
   * with `cart:refresh` rather than to navigate.
   *
   * A key already being removed is also refused up front, so a double click
   * cannot turn its own first request into a second one against a dead line.
   *
   * WHY THE HREF IS TAKEN AWAY RATHER THAN JUST PREVENTED
   *
   * Relying on `preventDefault` alone left an intermittent failure: measured on
   * staging over seven trials, the same click sometimes removed the line AND
   * navigated to the error page, and sometimes behaved perfectly, with no
   * difference in the code path taken. The network log for a failing trial shows
   * two `cart/change.js` POSTs followed by the GET, so a re-render — the cart
   * drawer is rebuilt by `cart:refresh` from assets/custom-gift.js adding or
   * removing the free gift, and again by `_onCartChanged` on a 1250ms timer —
   * is interleaving with the click in a way that a single prevented event
   * cannot cover reliably.
   *
   * Rather than keep racing it, the anchor stops being a navigable link the
   * moment this script is live: the href moves to `data-ns-cdv2-remove-href` and
   * the element becomes a `role="button"`. It ships as a real link, so the
   * control still works with JS off, and once JS is running there is no href for
   * anything — a stray event, a missed listener, a re-render — to follow. The
   * MutationObserver is what makes that hold, because every cart re-render
   * inserts fresh anchors with their href attribute back on.
   *
   * The cart PAGE's OWN line list is deliberately excluded — there the plain
   * navigation is the intended behaviour, so those links keep their href. That
   * exclusion is per-link (`usesPlainNavigation`), not per-page: the drawer
   * ships on /cart too, and it must behave the same there as everywhere else.
   */
  var removalsInFlight = {};
  var HREF_ATTR = 'data-ns-cdv2-remove-href';

  function onCartPage() {
    return !!(
      window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.pageType === 'cart'
    );
  }

  /*
   * The exclusion is per-LINK, not per-page. Only the cart PAGE's own line list
   * keeps its href — the drawer ships on every page including /cart, and a
   * shopper who opens it there should get the same in-place removal they get
   * everywhere else. Gating on `onCartPage()` alone caught the drawer too and
   * made its bin icon reload the page, which is what QA reported on 2026-08-14.
   */
  function usesPlainNavigation(link) {
    return onCartPage() && !link.closest('cart-drawer');
  }

  function takeOwnership(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var links = scope.querySelectorAll('.ns-cdv2__remove[href]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (usesPlainNavigation(link)) continue;
      link.setAttribute(HREF_ATTR, link.getAttribute('href'));
      link.removeAttribute('href');
      // An <a> with no href is neither focusable nor announced as actionable,
      // so the control has to carry those itself from here on.
      link.setAttribute('role', 'button');
      link.setAttribute('tabindex', '0');
    }
  }

  /*
   * The cart page's mobile footer is `position: fixed`, so it takes its height
   * out of the viewport without reserving any. The page pads its bottom by
   * `--ns-cdv2-page-footer-reserve` to compensate, and that number has to be
   * measured rather than assumed: the Shop Pay line only renders for qualifying
   * carts, the discount badge comes and goes with the cart total, and the trust
   * row wraps to a different number of lines across phone widths. A fixed
   * figure is wrong for some carts in one of two ways — too small strands the
   * last line item under the footer, too large leaves dead space.
   *
   * Writes `--ns-cdv2-page-footer-measured`, never the reserve itself, so the
   * desktop media query still decides what happens past the breakpoint. See the
   * matching note in assets/ns-cart-page-v2.css.
   *
   * `borderBoxSize` is preferred over the entry's `contentRect`, which excludes
   * padding — and the safe-area inset lives in this element's padding.
   */
  var footerObserver = null;
  var footerResizeBound = false;

  function watchFooterHeight() {
    // Re-entrant on purpose: adding from the drawer while on /cart swaps the
    // cart section's children, so the page and footer this closed over are
    // replaced by fresh elements and the old observer is left watching a
    // detached node. Callers re-run this after any such swap.
    if (footerObserver) {
      footerObserver.disconnect();
      footerObserver = null;
    }

    var page = document.querySelector('.ns-cdv2-page');
    var footer = page && page.querySelector('.ns-cdv2-page__footer');
    if (!page || !footer) return;

    function apply(blockSize) {
      page.style.setProperty(
        '--ns-cdv2-page-footer-measured',
        Math.ceil(typeof blockSize === 'number' ? blockSize : footer.getBoundingClientRect().height) + 'px'
      );
    }

    if (typeof ResizeObserver === 'undefined') {
      // No observer: measure once, and again on resize. Content-driven changes
      // are missed, but the reserve is still closer than a fixed guess. Bound
      // once — `apply` re-reads through the current closure on every call.
      apply();
      if (!footerResizeBound) {
        footerResizeBound = true;
        window.addEventListener('resize', function () {
          var current = document.querySelector('.ns-cdv2-page');
          var currentFooter = current && current.querySelector('.ns-cdv2-page__footer');
          if (!current || !currentFooter) return;
          current.style.setProperty(
            '--ns-cdv2-page-footer-measured',
            Math.ceil(currentFooter.getBoundingClientRect().height) + 'px'
          );
        });
      }
      return;
    }

    // Measure once, synchronously, before observing. A ResizeObserver's first
    // callback is delivered on the rendering lifecycle, so until a frame is
    // painted the reserve is still the 200px fallback — which showed up during
    // verification as the value simply never changing in a tab that was not
    // rendering. This also avoids a first paint at the wrong reserve.
    apply();

    footerObserver = new ResizeObserver(function (entries) {
      var box = entries[0] && entries[0].borderBoxSize && entries[0].borderBoxSize[0];
      apply(box ? box.blockSize : undefined);
    });
    footerObserver.observe(footer);
  }

  function watchForNewRemoveLinks() {
    if (typeof MutationObserver === 'undefined') return;

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) {
          takeOwnership(document);
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (!window.nsCdv2RemoveBound) {
    window.nsCdv2RemoveBound = true;

    takeOwnership(document);
    if (document.body) {
      watchForNewRemoveLinks();
      watchFooterHeight();
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        watchForNewRemoveLinks();
        watchFooterHeight();
      });
    }

    // Enter and Space have to be wired by hand, because role="button" describes
    // the element without giving it a button's key behaviour.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      var link = event.target.closest ? event.target.closest('.ns-cdv2__remove') : null;
      if (!link || !link.getAttribute(HREF_ATTR)) return;
      event.preventDefault();
      link.click();
    });

    document.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('.ns-cdv2__remove') : null;
      if (!link) return;

      var href = link.getAttribute(HREF_ATTR) || link.getAttribute('href');
      if (!href) return;

      // Leave the cart page's own line list on its full-page path. The drawer,
      // even when opened on top of /cart, is handled here like anywhere else.
      if (usesPlainNavigation(link)) return;

      var url;
      try {
        url = new URL(href, window.location.origin);
      } catch (error) {
        return; // Unparseable href: let the browser follow it.
      }
      var lineKey = url.searchParams.get('id');
      if (!lineKey) return;

      event.preventDefault();

      // A second click on a line already being removed would otherwise send a
      // second request against a line the first one is about to delete.
      if (removalsInFlight[lineKey]) return;
      removalsInFlight[lineKey] = true;

      removeLine(link, lineKey)
        .catch(function (error) {
          if (error && error.nsRequestNeverSent) {
            // Nothing reached the server, so the cart is untouched and the
            // plain link is still the right answer.
            console.error('[ns-cdv2] remove request never sent, following the link:', error);
            window.location.href = href;
            return;
          }

          // The server answered. The line is gone, or its state is unknown —
          // either way the href would now mutate the cart again or land on
          // "no valid id or line parameter". Re-read the cart instead.
          console.error('[ns-cdv2] remove could not be completed in place:', error);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .then(function () {
          delete removalsInFlight[lineKey];
        });
    });
  }

  async function removeLine(link, lineKey) {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    // `_onWillChange` reads `pillLoaderElement` without a null check, and a line
    // with no image renders no <pill-loader>. Only involve LineItem when the
    // loader it expects is actually there.
    var lineItem = link.closest('line-item');
    if (lineItem && !lineItem.querySelector('pill-loader')) lineItem = null;

    if (lineItem) {
      lineItem.dispatchEvent(
        new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity: 0 } })
      );
    }

    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    // Only a failure to REACH the server is allowed to fall back to the link,
    // so the two outcomes are separated here rather than at the call site.
    var response;
    try {
      response = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0, sections: sections }),
      });
    } catch (error) {
      error.nsRequestNeverSent = true;
      throw error;
    }

    if (!response.ok) {
      throw new Error('cart/change.js responded ' + response.status);
    }
    var cart = await response.json();

    if (lineItem) {
      lineItem.dispatchEvent(
        new CustomEvent('line-item:change', { bubbles: true, detail: { quantity: 0, cart: cart } })
      );
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'line-item:change', cart: cart },
      })
    );
  }

  if (customElements.get('ns-cdv2-upsell')) return;

  /*
   * ── keeping the card put across a cart change ────────────────────────────
   *
   * `_onCartChanged` in assets/theme.js.liquid replaces `.cart-drawer__inner`
   * wholesale on every cart change, so the upsell block is destroyed and
   * re-rendered by the add that the upsell itself performed. Two things came
   * back wrong (owner report 2026-08-14):
   *
   *   1. The optimistic ADDED state vanished, because it lived on attributes of
   *      the nodes that had just been thrown away.
   *   2. The card showed a DIFFERENT product. snippets/ns-cdv2-upsell.liquid
   *      deliberately refuses to upsell something already in the cart — and the
   *      shopper had just put this product in the cart — so the pinned product
   *      fell through the chain to the next candidate.
   *
   * (2) is the part worth being careful about: that already-in-cart rule is
   * correct for a FIRST render and wrong only for the re-render immediately
   * after an add, and the server cannot tell those apart. So it is left alone
   * and the card is preserved on the client instead: the node is held across
   * the swap and re-adopted by whichever element connects next.
   *
   * The hold is released as soon as the variant it advertises leaves the cart,
   * which hands the block back to normal server control the moment the shopper
   * removes the item.
   *
   * `scope` exists because the cart PAGE renders its own upsell while the
   * drawer is also on the page; without it, the page's element could adopt a
   * card the drawer was holding.
   *
   * The cart page needs one extra step. Its add path reloads (see `add()`), so
   * an in-memory node cannot survive — the card's HTML is handed to
   * sessionStorage instead and claimed once on the way back. That entry is
   * consumed on first read and ignored after HANDOFF_MAX_AGE_MS, so a shopper
   * who wanders off and returns to /cart later gets a normal server render
   * rather than a stale ADDED card.
   */
  var HANDOFF_KEY = 'nsCdv2UpsellHandoff';
  var HANDOFF_MAX_AGE_MS = 15000;

  var preserved = null; // { card: Element, variantId: Number, scope: String }

  function upsellScope(element) {
    return element.closest('cart-drawer') ? 'drawer' : 'page';
  }

  function writeHandoff(card, variantId) {
    try {
      window.sessionStorage.setItem(
        HANDOFF_KEY,
        JSON.stringify({ html: card.outerHTML, variantId: variantId, at: Date.now() })
      );
    } catch (error) {
      // Private modes throw. The reload then just re-renders normally.
      console.warn('[ns-cdv2] could not hand the upsell card across the reload:', error);
    }
  }

  function claimHandoff() {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(HANDOFF_KEY);
      window.sessionStorage.removeItem(HANDOFF_KEY);
    } catch (error) {
      console.warn('[ns-cdv2] could not read the upsell handoff:', error);
      return null;
    }
    if (!raw) return null;

    var entry;
    try {
      entry = JSON.parse(raw);
    } catch (error) {
      return null;
    }
    if (!entry || !entry.html || Date.now() - entry.at > HANDOFF_MAX_AGE_MS) return null;

    var parsed = new DOMParser().parseFromString(entry.html, 'text/html');
    var card = parsed.querySelector('[data-ns-cdv2-card]');
    if (!card) return null;

    return { card: card, variantId: Number(entry.variantId), scope: 'page' };
  }

  document.addEventListener('cart:change', function (event) {
    if (!preserved) return;
    var cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) return;

    var stillInCart = cart.items.some(function (item) {
      return Number(item.variant_id) === preserved.variantId;
    });
    if (!stillInCart) preserved = null;
  });

  customElements.define(
    'ns-cdv2-upsell',
    class NsCdv2Upsell extends HTMLElement {
      connectedCallback() {
        this._onClick = this._onClick.bind(this);
        this.addEventListener('click', this._onClick);

        var scope = upsellScope(this);
        if (!preserved && scope === 'page') preserved = claimHandoff();

        // A held card is the final answer, exactly like a merchant-pinned one:
        // adopt it and never fetch a recommendation over the top.
        if (preserved && preserved.scope === scope) {
          this.replaceChildren(preserved.card);
          return;
        }

        this.load();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this._aborted = true;
      }

      get productId() {
        var stored = null;
        try {
          stored = window.localStorage.getItem('nsCdv2LastAddedProductId');
        } catch (error) {
          console.warn('[ns-cdv2] could not read last added product:', error);
        }
        return stored || this.dataset.lastProductId || '';
      }

      async load() {
        // A merchant-pinned upsell is the final answer — never fetch over it.
        if (this.dataset.pinned === 'true') return;

        var productId = this.productId;
        if (!productId) return;

        for (var intent of ['complementary', 'related']) {
          var card = await this.fetchCard(productId, intent);
          if (this._aborted) return;
          if (card) {
            // Replace the server-rendered collection fallback only once a real
            // recommendation is in hand — never blank it first, or an empty
            // response would leave the block worse off than before JS ran.
            this.replaceChildren(card);
            return;
          }
        }
      }

      /*
       * Fetches the card as RENDERED LIQUID, not JSON. `section_id` makes
       * Shopify run sections/ns-cdv2-upsell.liquid for this request, which is
       * what gives the card access to per-product metafields (the eyebrow) and
       * lets the server decide "exactly one" against the live cart.
       * /recommendations/products.json returns no metafields, so the JSON path
       * cannot render this card.
       */
      async fetchCard(productId, intent) {
        var root =
          window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';
        var url = root + 'recommendations/products?section_id=ns-cdv2-upsell&product_id=' +
          encodeURIComponent(productId) + '&limit=10&intent=' + intent;

        var markup;
        try {
          var response = await fetch(url);
          if (!response.ok) {
            throw new Error('recommendations ' + intent + ' responded ' + response.status);
          }
          markup = await response.text();
        } catch (error) {
          console.warn('[ns-cdv2] recommendations fetch failed:', error);
          return null;
        }

        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        return parsed.querySelector('[data-ns-cdv2-card]');
      }

      _onClick(event) {
        var button = event.target.closest('[data-ns-cdv2-add]');
        if (!button || button.hasAttribute('data-ns-cdv2-added')) return;

        var variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;

        button.setAttribute('data-ns-cdv2-added', 'true');
        var card = button.closest('[data-ns-cdv2-card]');
        if (card) card.setAttribute('data-ns-cdv2-added', 'true');
        var label = button.querySelector('.ns-cdv2__card-add-label');
        if (label) label.textContent = this.dataset.addedLabel || label.textContent;

        // Hold this card, in the state it is in right now, so the re-render the
        // add is about to trigger puts the same ADDED card back. On the cart
        // page that re-render is a reload, so the HTML goes to sessionStorage
        // as well — written here, before the request, because `add()` reloads
        // the moment it returns.
        if (card) {
          var scope = upsellScope(this);
          preserved = { card: card, variantId: Number(variantId), scope: scope };
          if (scope === 'page') writeHandoff(card, Number(variantId));
        }

        this.add(Number(variantId)).catch(function (error) {
          // Nothing was added, so nothing should be held claiming otherwise.
          preserved = null;
          try {
            window.sessionStorage.removeItem(HANDOFF_KEY);
          } catch (storageError) {
            console.warn('[ns-cdv2] could not clear the upsell handoff:', storageError);
          }
          console.error('[ns-cdv2] upsell add failed:', error);
        });
      }

      /*
       * v2 owns its add path instead of calling the theme's `upsellATC`, which
       * fires FOUR requests for one add: /cart/add.js (rendering the
       * `variant-added` section nobody in v2 reads), /cart.js, and then the
       * cart-drawer section fetched TWICE — once by the `cart:refresh` listener
       * and once by an explicit `_onCartRefresh()` call. It then re-inits the v1
       * upsell swiper on 1000ms, 1500ms and 2500ms timers. That is where the
       * one-to-two-second wait came from.
       *
       * This path is two requests. The add call asks for exactly the sections
       * the page's own components declare through `cart:prepare-bundled-sections`
       * (CartDrawer pushes its own id), so `cart:change` can hand the drawer
       * finished markup and no third fetch is needed.
       */
      async add(variantId) {
        var root =
          window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';

        var sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sections },
          })
        );

        /*
         * Which surface the shopper actually pressed, not which page they are
         * on — the same distinction D1 turned on. The drawer ships on /cart
         * too, and an add from it must behave there as it does everywhere else.
         *
         * Only the DRAWER case needs the cart page's section bundled in: the
         * page cannot reload out from under an open drawer, so its markup has
         * to be swapped instead, and asking for it here keeps that on the same
         * single request. The page's own upsell still reloads, so it does not
         * pay for a section it is about to throw away.
         *
         * Nothing listens for `cart:prepare-bundled-sections` on behalf of this
         * section — theme.js's only listener is CartDrawer registering itself —
         * so the id has to be pushed by hand.
         */
        var scope = upsellScope(this);
        var pageSectionId = null;
        if (onCartPage() && scope === 'drawer') {
          var pageSection = document.querySelector('.shopify-section--main-cart');
          if (pageSection && pageSection.id) {
            pageSectionId = pageSection.id.replace(/^shopify-section-/, '');
            if (sections.indexOf(pageSectionId) === -1) sections.push(pageSectionId);
          }
        }

        var addResponse = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections }),
        });
        if (!addResponse.ok) {
          throw new Error('cart/add.js responded ' + addResponse.status);
        }
        var added = await addResponse.json();

        /*
         * The cart PAGE's own upsell still reloads. Its markup is
         * server-rendered, there is no drawer open to preserve, and it is what
         * the theme already does for a quantity change here (LineItemQuantity
         * navigates to /cart/change when pageType is "cart"). Reloading skips
         * the /cart.js round trip entirely, and `writeHandoff` has already put
         * the ADDED card in sessionStorage to survive it.
         *
         * The DRAWER on /cart deliberately falls through instead. Reloading
         * would close it mid-interaction and contradict its behaviour on every
         * other page; the page behind is refreshed below instead.
         */
        if (onCartPage() && scope !== 'drawer') {
          window.location.reload();
          return;
        }

        /*
         * Refresh the cart page sitting behind the open drawer. Without this it
         * keeps the totals and line list it was server-rendered with, so
         * closing the drawer reveals a cart that is missing what was just added.
         *
         * Replacing the children rather than the element keeps the section
         * wrapper — and its `{% style %}` custom properties — in place. The
         * remove links inside the new markup are adopted by the MutationObserver
         * in `watchForNewRemoveLinks`, which since D1 runs on /cart too.
         */
        if (pageSectionId && added.sections && added.sections[pageSectionId]) {
          var host = document.getElementById('shopify-section-' + pageSectionId);
          var fresh = new DOMParser()
            .parseFromString(added.sections[pageSectionId], 'text/html')
            .getElementById('shopify-section-' + pageSectionId);
          if (host && fresh) {
            host.replaceChildren.apply(host, Array.prototype.slice.call(fresh.childNodes));
            // The footer that was being measured is now detached; re-bind.
            watchFooterHeight();
          }
        }

        var cart = await (await fetch(root + 'cart.js')).json();
        cart.sections = added.sections;

        // `cart:change` before `variant:add`, so the drawer opens already
        // showing the added item rather than opening and then swapping.
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cart },
          })
        );

        /*
         * Dispatched on documentElement so it reaches CartDrawer's listener on
         * `document` — that listener is what opens the drawer. See the matching
         * comment in sections/cart-drawer.liquid.
         */
        document.documentElement.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: cart.items, cart: cart },
          })
        );
      }
    }
  );
})();
